/**
 * /balance-sheet — A = L + E snapshot as of a date.
 *
 * The classic accountant's view: pick a date, get the live balance of
 * every postable account in the asset/liability/equity sections,
 * grouped by section with a total at the bottom of each, plus a
 * "balanced?" indicator at the bottom (Total Assets vs Total L+E).
 *
 * The backend computes balances from posted journal lines through the
 * chosen date — so the report reflects the accrual-basis ledger.
 *
 * Print-friendly layout: clean white card, mono amounts, section
 * headers reading like a printed statement. Operators routinely
 * Cmd+P this page for monthly close packs.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  balanceSheetQueryOptions,
  consolidatedBalanceSheetQueryOptions,
  myEntitiesQueryOptions,
} from "@/lib/queries";
import { FsSection, fmtMoney } from "@/components/FinancialStatementSection";

export const Route = createFileRoute("/_auth/balance-sheet")({
  component: BalanceSheetPage,
});

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function BalanceSheetPage() {
  const [asOfDate, setAsOfDate] = useState(todayIso);
  const [consolidated, setConsolidated] = useState(false);

  // Only show the Consolidated toggle when the current entity is a
  // consolidating parent — otherwise there are no subs to roll up.
  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  // We don't have is_consolidating_parent in AvailableEntity yet — treat
  // any entity the user is admin in as potentially a parent. If the
  // server rejects consolidated, we'll see the error inline.
  const canConsolidate = currentEntity?.role === "admin";

  const entityBsQuery = useQuery({
    ...balanceSheetQueryOptions(asOfDate),
    enabled: Boolean(asOfDate) && !consolidated,
  });
  const consolBsQuery = useQuery({
    ...consolidatedBalanceSheetQueryOptions(
      currentEntity?.id ?? "", asOfDate,
    ),
    enabled: Boolean(asOfDate) && consolidated && Boolean(currentEntity?.id),
  });
  const bsQuery = consolidated ? consolBsQuery : entityBsQuery;
  const bs = bsQuery.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      {/* Header / controls */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 print:hidden dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Balance Sheet</h1>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Snapshot of all postable accounts as of the chosen date · accrual basis
          </span>
          <div className="flex-1" />
          {canConsolidate && (
            <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer dark:text-slate-300"
                   title="Roll up this entity + all subsidiaries, eliminating intercompany Due-from/Due-to pairs">
              <input
                type="checkbox"
                checked={consolidated}
                onChange={(e) => setConsolidated(e.target.checked)}
              />
              Consolidated
            </label>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            As of:
            <input
              type="date"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 dark:border-gray-700"
            />
          </label>
          <button
            onClick={() => window.print()}
            className="text-sm border border-slate-300 hover:bg-slate-100 rounded-md px-3 py-1.5 dark:border-gray-700 dark:hover:bg-gray-800"
            title="Print or save as PDF"
          >
            🖨 Print
          </button>
          {bsQuery.isFetching && (
            <span className="text-xs text-slate-400 dark:text-slate-500">Loading…</span>
          )}
        </div>
      </div>

      {/* Statement body */}
      <div className="flex-1 min-h-0 overflow-auto">
        <div className="max-w-3xl mx-auto px-6 py-8 print:py-4">
          {bsQuery.error && (
            <div className="text-sm text-red-600">
              Error: {(bsQuery.error as Error).message}
            </div>
          )}

          {bs && (
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 print:border-0 print:shadow-none print:p-0 dark:bg-gray-900 dark:border-gray-800">
              {/* Statement header (printable) */}
              <div className="text-center mb-6 pb-4 border-b border-slate-300 dark:border-gray-700">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                  {consolidated ? "Consolidated Balance Sheet" : "Balance Sheet"}
                </h2>
                {consolidated && currentEntity && (
                  <p className="text-xs text-slate-500 mt-0.5 dark:text-slate-400">
                    {currentEntity.display_name} + subsidiaries · intercompany eliminated
                  </p>
                )}
                <p className="text-sm text-slate-600 mt-1 dark:text-slate-300">
                  As of {bs.as_of}
                </p>
              </div>

              {/* Assets */}
              <FsSection section={bs.assets} />

              {/* Liabilities */}
              <FsSection section={bs.liabilities} />

              {/* Equity */}
              <FsSection section={bs.equity} />

              {/* Bottom-line totals */}
              <div className="mt-6 pt-3 border-t-2 border-slate-900 space-y-1">
                <div className="flex items-baseline">
                  <strong className="text-sm uppercase tracking-wide text-slate-900 dark:text-slate-100">
                    Total Assets
                  </strong>
                  <span className="flex-1" />
                  <span className="font-mono font-semibold text-slate-900 text-base dark:text-slate-100">
                    {fmtMoney(bs.total_assets)}
                  </span>
                </div>
                <div className="flex items-baseline">
                  <strong className="text-sm uppercase tracking-wide text-slate-900 dark:text-slate-100">
                    Total Liabilities + Equity
                  </strong>
                  <span className="flex-1" />
                  <span className="font-mono font-semibold text-slate-900 text-base dark:text-slate-100">
                    {fmtMoney(bs.total_liabilities_and_equity)}
                  </span>
                </div>

                {/* Balanced indicator */}
                <div className="mt-3 pt-3 border-t border-slate-200 dark:border-gray-800">
                  {bs.is_balanced ? (
                    <div className="text-sm text-emerald-700 flex items-center gap-2">
                      <span>✓</span>
                      <span>
                        <strong>Balanced.</strong> Assets = Liabilities + Equity.
                      </span>
                    </div>
                  ) : (
                    <div className="text-sm text-red-700 flex items-center gap-2">
                      <span>✗</span>
                      <span>
                        <strong>Out of balance!</strong> Difference:{" "}
                        {fmtMoney(
                          (
                            Number(bs.total_assets) -
                            Number(bs.total_liabilities_and_equity)
                          ).toString(),
                        )}
                        . Open a journal entry investigation.
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {bsQuery.isLoading && !bs && (
            <div className="text-sm text-slate-500 text-center py-8 dark:text-slate-400">
              Loading balance sheet…
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
